import {
  error,
  progress,
  type StreamStep,
  type UploadAction,
  uploadComplete,
} from "./uploadTypes";
import {
  concat,
  parseNDJSON,
  readableStreamToAsyncGenerator,
} from "./uploadUtils";

export function uploadWorkspace(
  file: File,
  transferServiceUrl: string,
  authorization: string,
  signal: AbortSignal,
): AsyncGenerator<UploadAction> {
  const formData = new FormData();
  formData.append("file", file);

  const xhr = new XMLHttpRequest();

  const actions = getUploadWorkspaceActions(xhr, signal);

  xhr.open("POST", `${transferServiceUrl}/upload/Progress/`, true);
  xhr.setRequestHeader("Authorization", authorization);
  xhr.send(formData);

  return actions;
}

function getUploadWorkspaceActions(
  xhr: XMLHttpRequest,
  signal: AbortSignal,
): AsyncGenerator<UploadAction> {
  const uploadStream = createUploadStream(xhr);
  const downloadStream = createDownloadStream(xhr);
  const actions = readableStreamToAsyncGenerator(
    concat(uploadStream, downloadStream),
    signal,
  );
  return actions;
}

function createUploadStream(xhr: XMLHttpRequest): ReadableStream<UploadAction> {
  return new ReadableStream<UploadAction>({
    start(controller) {
      function enqueueProgress(event: ProgressEvent) {
        const action = progress({
          processed: event.loaded,
          total: event.total,
          step: "Uploading",
        });
        controller.enqueue(action);
      }
      xhr.upload.addEventListener("loadstart", enqueueProgress);
      xhr.upload.addEventListener("progress", enqueueProgress);

      xhr.upload.addEventListener("load", () => {
        controller.close();
      });

      xhr.upload.addEventListener("error", () => {
        controller.error(new Error("Upload failed."));
      });
    },
    cancel() {
      xhr.abort();
    },
  });
}

function createDownloadStream(
  xhr: XMLHttpRequest,
): ReadableStream<UploadAction> {
  return new ReadableStream<string>({
    start(controller) {
      let last_index = 0;
      xhr.addEventListener("progress", () => {
        if (xhr.status !== 200) {
          return;
        }
        const responseText = xhr.responseText;
        const curr_index = responseText.length;
        if (last_index === curr_index) {
          return;
        }
        const chunk = responseText.substring(last_index, curr_index);
        last_index = curr_index;
        controller.enqueue(chunk);
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 200) {
          controller.close();
        } else {
          controller.error(
            new Error(`${xhr.status} ${xhr.statusText} ${xhr.responseText}`),
          );
        }
      });

      xhr.addEventListener("error", () => {
        controller.error(new Error(`Request failed.`));
      });
    },
    cancel() {
      xhr.abort();
    },
  })
    .pipeThrough(parseNDJSON())
    .pipeThrough(toUploadAction());
}

function toUploadAction(): TransformStream<any, UploadAction> {
  return new TransformStream<any, UploadAction>({
    transform(step, controller) {
      if (!isStreamStep(step)) {
        console.log(step);
        return;
      }
      switch (step.type) {
        case "detailed_progress":
          controller.enqueue(progress(step.value));
          break;
        case "complete":
          controller.enqueue(uploadComplete(step.value.workspaceId));
          break;
        case "error":
          controller.enqueue(error(step.value));
          break;
        case "progress":
        default:
          console.log(step);
          break;
      }
    },
  });
}

const isStreamStep = (input: any): input is StreamStep =>
  input && "type" in input && "value" in input;
